import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import i18n from './i18n';

export const UserContext = createContext(null);

const USER_SESSION_HINT_KEY = 'tenryu_user_session_hint';
const ADMIN_SESSION_HINT_KEY = 'tenryu_admin_session_hint';

function setUserSessionHint(on) {
  try {
    if (on) localStorage.setItem(USER_SESSION_HINT_KEY, '1');
    else localStorage.removeItem(USER_SESSION_HINT_KEY);
  } catch (_) {}
}

function clearAdminSessionHint() {
  try {
    localStorage.removeItem(ADMIN_SESSION_HINT_KEY);
  } catch (_) {}
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check login status on mount
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const res = await axios.get('/api/user/me');
      if (res.data.authenticated) {
        setUser(res.data.user);
        setUserSessionHint(true);
        // 普通用户登录态下，确保不会残留 admin hint（避免导航栏误显示“用户管理”等管理入口）
        clearAdminSessionHint();
        // Sync header
        try {
          window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
            detail: { isAdmin: false, isLoggedIn: true } 
          }));
        } catch (_) {}
      } else {
        setUser(null);
        setUserSessionHint(false);
        try {
          window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
            detail: { isAdmin: false, isLoggedIn: false } 
          }));
        } catch (_) {}
      }
    } catch (e) {
      console.error('Check user error:', e);
      setUser(null);
      setUserSessionHint(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/user/login', { email, password });
    if (res.data.success) {
      setUser(res.data.user);
      setUserSessionHint(true);
      clearAdminSessionHint();
      // Update header status
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          detail: { isAdmin: false, isLoggedIn: true }
        }));
      } catch (_) {}
      return true;
    }
    return false;
  };

  const sendEmailVerificationCode = async (email) => {
    const res = await axios.post(
      '/api/auth/send-email-verification',
      { email },
      { headers: { 'x-ui-lang': i18n?.language || '' } }
    );
    return res.data;
  };

  const register = async (username, email, password, email_code) => {
    const res = await axios.post('/api/auth/register', { username, email, password, email_code });
    return res.data.success;
  };

  const forgotPassword = async (email) => {
    const res = await axios.post(
      '/api/auth/forgot-password',
      { email },
      { headers: { 'x-ui-lang': i18n?.language || '' } }
    );
    return res.data;
  };

  const verifyResetCode = async (email, email_code) => {
    const res = await axios.post(
      '/api/auth/verify-password-reset-code',
      { email, email_code },
      { headers: { 'x-ui-lang': i18n?.language || '' } }
    );
    return res.data;
  };

  const resetPassword = async (email, email_code, newPassword) => {
    const res = await axios.post(
      '/api/auth/reset-password',
      { email, email_code, newPassword },
      { headers: { 'x-ui-lang': i18n?.language || '' } }
    );
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/user/logout');
    } catch (e) {
      console.error('Logout error', e);
    } finally {
      setUser(null);
      setUserSessionHint(false);
      // Update header status
      try {
        window.dispatchEvent(new CustomEvent('app:admin-status-changed', { 
          detail: { isAdmin: false, isLoggedIn: false } 
        }));
      } catch (_) {}
    }
  };

  return (
    <UserContext.Provider value={{ user, loading, login, register, sendEmailVerificationCode, forgotPassword, verifyResetCode, resetPassword, logout, checkUser }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
